<template>
  <div class="max-w-content-container relative mx-auto min-h-[500px]">
    <div v-if="isLoading" class="absolute left-0 w-full lg:top-1/2">
      <TheLoader />
    </div>

    <div v-else class="flex flex-col gap-5">
      <div
        v-if="isAuthenticated && reports.length"
        class="mb-6 lg:absolute lg:right-0 lg:-mt-[62px]"
      >
        <SharedCommonButton
          v-if="showDownloadAll"
          text="Download all"
          class="!w-full whitespace-nowrap !text-sm lg:!w-auto"
          event-id="button-download-all"
        >
          <IconDownload class="z-index w-full min-w-[16px] max-w-[16px]" />
        </SharedCommonButton>
      </div>

      <div v-if="!reports.length" class="text-center text-xl">
        There are currently no reports published, please check back later
      </div>

      <div v-if="reports.length">
        <h2
          v-if="hasActiveReports && !hasAccessToAllReports"
          class="mb-3 text-lg font-semibold"
          data-testid="active-reports-title"
        >
          Purchased Subscriptions
        </h2>

        <div
          class="grid grid-cols-1 items-center justify-center gap-4 md:grid-cols-2"
        >
          <div v-for="(report, index) in activeReports" :key="index">
            <SharedMarketVideoCard
              v-if="report?.tags[0]?.name === 'forecast-video'"
              :report="report"
              :initial-fields="initialFields"
              :product="slug"
            />
            <SharedMarketReportCard
              v-else
              :report="report"
              :category="forecastSubscription"
              :initial-fields="initialFields"
            />
          </div>
        </div>

        <div v-if="availableReports.length" class="mt-3">
          <div v-if="!hasAccessToNoReports">
            <h2
              class="text-lg font-semibold"
              data-testid="available-reports-title"
            >
              Available Subscriptions
            </h2>
            <p class="mt-1 mb-3">
              For more information on our additional subscriptions, contact
              Benchmark’s forecasting specialists today.
            </p>
          </div>

          <div
            class="grid grid-cols-1 items-center justify-center gap-4 md:grid-cols-2"
          >
            <div v-for="(report, index) in availableReports" :key="index">
              <SharedMarketVideoCard
                v-if="report?.tags[0]?.name === 'forecast-video'"
                :report="report"
                :initial-fields="initialFields"
                :product="slug"
                :use-gray-scale-background="hasActiveReports"
              />
              <SharedMarketReportCard
                v-else
                :report="report"
                :category="forecastSubscription"
                :initial-fields="initialFields"
                :use-gray-scale-background="hasActiveReports"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TReportCard } from '~/src/types/reports'

import useAuthentication from '~/composables/useAuthentication'

import { FORECASTS } from '~/constants/services/constants'

import TheLoader from '~/components/TheLoader.vue'
import SharedCommonButton from '~/components/shared/common/Button.vue'
import SharedMarketReportCard from '~/components/shared/market/ReportCard.vue'
import SharedMarketVideoCard from '~/components/shared/market/VideoCard.vue'

import IconDownload from '~/assets/icons/icon-download.svg'

const props = withDefaults(
  defineProps<{
    slug: string
    forecastSubscription?: string
    isLoading?: boolean
    showDownloadAll?: boolean
    reports?: TReportCard[]
    activeReports?: TReportCard[]
    availableReports?: TReportCard[]
  }>(),
  {
    forecastSubscription: '',
    isLoading: true,
    showDownloadAll: false,
    reports: () => [],
    activeReports: () => [],
    availableReports: () => [],
  },
)

const { isAuthenticated } = useAuthentication()

const hasActiveReports = computed(() => props.activeReports?.length > 0)
const hasAccessToNoReports = computed(
  () => props.availableReports?.length === props.reports?.length,
)
const hasAccessToAllReports = computed(
  () => props.activeReports?.length === props.reports?.length,
)
const initialFields = computed(() => [props.slug, FORECASTS.checkbox])
</script>
